import { authService } from '@/api/services/authService';
import { HealthProvider } from '@/contexts/HealthContext';
import { featureFlags, shouldStartMsw } from '@/lib/featureFlags';
import { onPerformanceMetric, performanceMonitor } from '@/lib/performance';
import { registerProductionServiceWorker } from '@/lib/pwaRegister';
import { buildPersistOptions } from '@/lib/queryPersist';
import '@fontsource/architects-daughter/400.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Initialize performance monitoring
performanceMonitor.initialize();

// Log performance metrics in development
if (import.meta.env.DEV) {
  onPerformanceMetric((metric) => {
    // eslint-disable-next-line no-console -- dev-only performance instrumentation
    console.log(`[Performance] ${metric.name}:`, metric.value, `(${metric.rating})`);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache for 10 minutes
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Silently revalidate cached data when the network returns
    },
  },
});

// Persist the cache to IndexedDB so warm starts paint last-known data instantly
// (and survive a flaky network) instead of cold-spinning every screen.
const persistOptions = buildPersistOptions(authService.getUserId());

async function startApp() {
  registerProductionServiceWorker();

  if (shouldStartMsw()) {
    if (featureFlags.useMock) {
      console.warn('Mock service worker is enabled with improved data quality.');

      // Set up a mock token for authentication in full mock mode
      if (!authService.getToken()) {
        authService.setToken('mock-jwt-token');
        const mockUserInfo = {
          googleSub: 'mock-user-123',
          email: 'user@example.com',
          emailVerified: true,
          name: 'John Doe',
          firstName: 'John',
          lastName: 'Doe',
          profilePicture: 'https://via.placeholder.com/150',
          locale: 'en',
          provider: 'google' as const,
          lastLogin: new Date().toISOString(),
        };
        localStorage.setItem('user_info', JSON.stringify(mockUserInfo));
      }
    } else if (featureFlags.mockFlowPlanning) {
      console.warn(
        'Flow planning MSW is enabled (VITE_MOCK_FLOW_PLANNING=true). Only /api/plans/* is mocked.'
      );
    }

    const [{ setupWorker }, mockOverrides] = await Promise.all([
      import('msw/browser'),
      import('@/api/mock-overrides'),
    ]);

    const handlers = featureFlags.useMock
      ? mockOverrides.getAllImprovedMocks()
      : mockOverrides.getFlowPlanningMocks();

    const worker = setupWorker(...handlers);

    try {
      await worker.start({
        onUnhandledRequest: 'bypass',
        serviceWorker: {
          url: '/mockServiceWorker.js',
        },
      });
    } catch (error) {
      console.error('Failed to start mock service worker:', error);
    }
  } else {
    console.warn(
      'MSW is off. Set VITE_USE_MOCK=true or VITE_MOCK_FLOW_PLANNING=true to mock APIs.'
    );
  }

  // Render the app after MSW is ready
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <HealthProvider>
          <App />
        </HealthProvider>
      </PersistQueryClientProvider>
    </React.StrictMode>
  );
}

// Start the app
startApp().catch(console.error);
